// adminfrontend/src/admin_ui/pages/AdminUsersPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import adminUsersApi from "../services/adminUsersApi";
import "../styles/Admin.css";

function AdminUsersPage({ adminEmail, onLogout }) {
  const [view, setView] = useState("users"); // "users" | "deleted"
  // create form
  const [roleName, setRoleName] = useState("");
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState("active");
  const [manufacturerId, setManufacturerId] = useState("");

  // lookups
  const [roles, setRoles] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);

  // list filters
  const [q, setQ] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // list
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(true);
  const [mutating, setMutating] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [toastTimer, setToastTimer] = useState(null);

  const [limit] = useState(100);
  const [offset] = useState(0);

  const normalizedRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);
  const roleIsManufacturer = roleName === "manufacturer_admin";

  function showToast(msg) {
    setOkMsg(msg);
    if (toastTimer) clearTimeout(toastTimer);
    const t = setTimeout(() => setOkMsg(""), 3000);
    setToastTimer(t);
  }

  async function loadLookups() {
    try {
      const data = await adminUsersApi.getLookups();
      if (!data || data.ok !== true) throw new Error("lookups_failed");
      setRoles(Array.isArray(data.roles) ? data.roles : []);
      setManufacturers(Array.isArray(data.manufacturers) ? data.manufacturers : []);
      // default role
      if (!roleName && Array.isArray(data.roles) && data.roles.length) {
        const preferred =
          data.roles.find((r) => r.name === "purchaser") ||
          data.roles.find((r) => r.name === "inventory_clerk") ||
          data.roles[0];
        setRoleName(preferred?.name || "");
      }
    } catch (e) {
      console.error("[AdminUsersPage] lookups error:", e);
      setRoles([]);
      setManufacturers([]);
    }
  }

  async function loadUsers() {
    setLoading(true);
    setError("");
    try {
      const data = await adminUsersApi.listUsers({
        limit,
        offset,
        q: q || undefined,
        role: filterRole || undefined,
        status: filterStatus || undefined,
      });

      if (!data || data.ok !== true) throw new Error("users_failed");
      setRows(Array.isArray(data.users) ? data.users : []);
      setTotal(Number(data.total || 0));
    } catch (e) {
      console.error("[AdminUsersPage] list error:", e);
      setError("Unable to load users. Check login or server.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  // deleted list state
  const [deletedRows, setDeletedRows] = useState([]);
  const [deletedTotal, setDeletedTotal] = useState(0);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [dq, setDQ] = useState("");
  const [dRole, setDRole] = useState("");
  const [dStatus, setDStatus] = useState("");

  const normalizedDeletedRows = useMemo(
    () => (Array.isArray(deletedRows) ? deletedRows : []),
    [deletedRows]
  );

  async function loadDeletedUsers() {
    setDeletedLoading(true);
    setError("");
    try {
      const data = await adminUsersApi.listDeletedUsers({
        limit,
        offset,
        q: dq || undefined,
        role: dRole || undefined,
        status: dStatus || undefined,
      });
      if (!data || data.ok !== true) throw new Error("deleted_users_failed");
      setDeletedRows(Array.isArray(data.users) ? data.users : []);
      setDeletedTotal(Number(data.total || 0));
    } catch (e) {
      console.error("[AdminUsersPage] deleted list error:", e);
      setError("Unable to load deleted users. Check server/login.");
      setDeletedRows([]);
      setDeletedTotal(0);
    } finally {
      setDeletedLoading(false);
    }
  }

  useEffect(() => {
    loadLookups();
    if (view === "deleted") loadDeletedUsers();
    else loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onCreate(e) {
    e.preventDefault();
    setMutating(true);
    setError("");
    setOkMsg("");

    try {
        // Client-side checks (avoid "button disabled does nothing" and catch autofill issues)
      const emailVal = (email || "").trim();
      const passVal = password || "";
      const roleVal = (roleName || "").trim();

      if (!roleVal) {
        setError("Role is required.");
        setMutating(false);
        return;
      }
      if (!emailVal) {
        setError("Email is required.");
        setMutating(false);
        return;
      }
      // lightweight email check (backend also validates with zod)
      if (!emailVal.includes("@")) {
        setError("Enter a valid email.");
        setMutating(false);
        return;
      }
      if (passVal.length < 8) {
        setError("Password must be at least 8 characters.");
        setMutating(false);
        return;
      }
      if (roleVal === "manufacturer_admin" && !manufacturerId) {
        setError("Manufacturer is required for manufacturer_admin.");
        setMutating(false);
        return;
      }
      const payload = {
        email: emailVal,
        password: passVal,
        displayName: displayName.trim() || undefined,
        roleName: roleVal,
        status,
        manufacturerId: roleVal === "manufacturer_admin" ? Number(manufacturerId) : undefined,
      };

      const data = await adminUsersApi.createUser(payload);
      if (!data || data.ok !== true) throw new Error("create_failed");

      setOkMsg("User created.");
      setEmail("");
      setDisplayName("");
      setPassword("");
      setStatus("active");
      setManufacturerId("");
      await loadUsers();
    } catch (e) {
      const apiErr = e?.response?.data?.error;
      if (apiErr === "email_taken") setError("Email already taken.");
      else if (apiErr === "role_not_found") setError("Role not found. Seed roles first.");
      else if (apiErr === "manufacturer_required") setError("Manufacturer is required for manufacturer_admin.");
      else if (apiErr === "manufacturer_not_found") setError("Selected manufacturer not found.");
      else setError("Unable to create user. Check required fields and try again.");
      console.error("[AdminUsersPage] create error:", e);
    } finally {
      setMutating(false);
    }
  }

  function onApplyFilters() {
    setOkMsg("");
    if (view === "deleted") loadDeletedUsers();
    else loadUsers();
  }

  function onClearFilters() {
    setQ("");
    setFilterRole("");
    setFilterStatus("");
    setDQ("");
    setDRole("");
    setDStatus("");
    setOkMsg("");
    if (view === "deleted") loadDeletedUsers();
    else loadUsers();
  }

  // inline password reset
  const [resetOpenId, setResetOpenId] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [deleteBusyId, setDeleteBusyId] = useState(null);
  const [restoreBusyId, setRestoreBusyId] = useState(null);

  async function onResetPassword(userId) {
    if (!resetPassword || resetPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setResetBusy(true);
    setError("");
    setOkMsg("");
    try {
      const data = await adminUsersApi.resetPassword(userId, { password: resetPassword });
      if (!data || data.ok !== true) throw new Error("reset_failed");
      showToast("Password updated.");
      setResetPassword("");
      setResetOpenId(null);
    } catch (e) {
      console.error("[AdminUsersPage] reset error:", e);
      setError("Unable to reset password. Check server/login.");
    } finally {
      setResetBusy(false);
    }
  }

  // status dropdown (pessimistic)
  const [statusBusyId, setStatusBusyId] = useState(null);
  const [pendingStatusById, setPendingStatusById] = useState({});

  async function onChangeStatus(userId, nextStatus) {
    setError("");
    setOkMsg("");
    setStatusBusyId(userId);
    setPendingStatusById((m) => ({ ...m, [userId]: nextStatus }));
    try {
      const data = await adminUsersApi.updateStatus(userId, { status: nextStatus });
      if (!data || data.ok !== true) throw new Error("status_failed");

      setRows((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, status: nextStatus } : u))
      );
      showToast(`Status updated to "${nextStatus}".`);
    } catch (e) {
      const apiErr = e?.response?.data?.error;
      if (apiErr === "cannot_self") setError("You cannot change your own status.");
      else if (apiErr === "user_deleted") setError("Cannot change status: user is deleted.");
      else setError("Unable to update status. Check server/login.");
      console.error("[AdminUsersPage] status update error:", e);
    } finally {
      setStatusBusyId(null);
      setPendingStatusById((m) => {
        const copy = { ...m };
        delete copy[userId];
        return copy;
      });
    }
  }

  async function onSoftDeleteUser(userId, emailVal) {
    if (deleteBusyId) return;
    const ok = window.confirm(`Delete user ${emailVal}?\n\nThey will no longer be able to log in.`);
    if (!ok) return;

    setDeleteBusyId(userId);
    setError("");
    setOkMsg("");
    try {
      const data = await adminUsersApi.softDeleteUser(userId);
      if (!data || data.ok !== true) throw new Error("delete_failed");

      // remove from active list
      setRows((prev) => prev.filter((u) => u.id !== userId));
      setTotal((t) => Math.max(0, Number(t) - 1));
      if (resetOpenId === userId) setResetOpenId(null);
      showToast("User deleted.");
    } catch (e) {
      const apiErr = e?.response?.data?.error;
      if (apiErr === "cannot_self") setError("You cannot delete your own admin account.");
      else setError("Unable to delete user. Check server/login.");
      console.error("[AdminUsersPage] soft delete error:", e);
    } finally {
      setDeleteBusyId(null);
    }
  }

  async function onRestoreUser(userId, emailVal) {
    if (restoreBusyId) return;
    const ok = window.confirm(`Restore user ${emailVal}?`);
    if (!ok) return;

    setRestoreBusyId(userId);
    setError("");
    setOkMsg("");
    try {
      const data = await adminUsersApi.restoreUser(userId);
      if (!data || data.ok !== true) throw new Error("restore_failed");

      // remove from deleted list (restored users return to normal users list)
      setDeletedRows((prev) => prev.filter((u) => u.id !== userId));
      setDeletedTotal((t) => Math.max(0, Number(t) - 1));
      showToast("User restored.");
    } catch (e) {
      const apiErr = e?.response?.data?.error;
      if (apiErr === "cannot_self") setError("You cannot restore your own admin account.");
      else setError("Unable to restore user. Check server/login.");
      console.error("[AdminUsersPage] restore error:", e);
    } finally {
      setRestoreBusyId(null);
    }
  }

  async function onPermanentDeleteUser(userId, emailVal) {
    const ok = window.confirm(
      `PERMANENTLY delete user ${emailVal}?\n\nThis is irreversible.`
    );
    if (!ok) return;

    setError("");
    setOkMsg("");
    try {
      const data = await adminUsersApi.permanentDeleteUser(userId);
      if (!data || data.ok !== true) throw new Error("permanent_delete_failed");
      setDeletedRows((prev) => prev.filter((u) => u.id !== userId));
      setDeletedTotal((t) => Math.max(0, Number(t) - 1));
      showToast("User permanently deleted.");
    } catch (e) {
      const apiErr = e?.response?.data?.error;
      if (apiErr === "cannot_self") setError("You cannot permanently delete your own admin account.");
      else if (apiErr === "permanent_delete_blocked") {
        const msg = e?.response?.data?.message || "User cannot be permanently deleted.";
        const blockers = Array.isArray(e?.response?.data?.blockers) ? e.response.data.blockers : [];
        const lines = blockers
          .filter((b) => b && b.table && b.column && typeof b.count === "number")
          .slice(0, 6)
          .map((b) => `${b.table}.${b.column} (${b.count})`);
        setError(lines.length ? `${msg}\nStill referenced in: ${lines.join(", ")}` : msg);
      }
      else setError("Unable to permanently delete user. Check server/login.");
      console.error("[AdminUsersPage] permanent delete error:", e);
    }
  }

  return (
    <section className="ucx-section">
      <div className="ucx-container ucx-admin-page">
        <div className="ucx-admin-header">
          <div>
            <h1 className="ucx-admin-title">Create User</h1>
            <p className="ucx-admin-subtitle">
              Provision business users (purchaser/inventory/org admin/manufacturer) for the normal portal login.
            </p>
          </div>

          <div className="ucx-admin-header-right">
            {adminEmail && <span className="ucx-small">Signed in as {adminEmail}</span>}
            <div className="ucx-admin-header-actions">
              <button
                type="button"
                className="ucx-btn ucx-btn-outline"
                onClick={() => {
                  setOkMsg("");
                  if (view === "users") {
                    setView("deleted");
                    loadDeletedUsers();
                  } else {
                    setView("users");
                    loadUsers();
                  }
                }}
                disabled={loading || deletedLoading}
              >
                {view === "users" ? "Deleted Users" : "Back to Users"}
              </button>
              <button type="button" className="ucx-btn ucx-btn-outline" 
               onClick={() => (view === "users" ? loadUsers() : loadDeletedUsers())}
                disabled={view === "users" ? loading : deletedLoading}
              >
                {(view === "users" ? loading : deletedLoading) ? "Refreshing…" : "Refresh"}
              </button>
              <button type="button" className="ucx-admin-btn ucx-admin-btn-reject" onClick={onLogout}>
                Sign out
              </button>
            </div>
          </div>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Create User</div>

          <form className="ucx-admin-form" onSubmit={onCreate} noValidate>
            <div className="ucx-admin-form-grid ucx-admin-form-grid-3">
              <div className="ucx-admin-field">
                <label>Role *</label>
                <select
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  onInput={(e) => setRoleName(e.target.value)}
                  required
                >
                  <option value="" disabled>
                    Select role…
                  </option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.name}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ucx-admin-field">
                <label>Email *</label>
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onInput={(e) => setEmail(e.target.value)}
                  placeholder="user@email.com"
                  autoComplete="off"
                  required
                />
              </div>

              <div className="ucx-admin-field">
                <label>Display name</label>
                <input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Full name"
                />
              </div>
            </div>

            <div className="ucx-admin-form-grid ucx-admin-form-grid-3">
              <div className="ucx-admin-field">
                <label>Password *</label>
                <input
                  type="password"
                  value={password}
                  onInput={(e) => setPassword(e.target.value)}
                  placeholder="Min 8 chars"
                  autoComplete="new-password"
                  required
                />
              </div>

              <div className="ucx-admin-field">
                <label>Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                </select>
              </div>

              {roleIsManufacturer ? (
                <div className="ucx-admin-field">
                  <label>Manufacturer *</label>
                  <select
                    value={manufacturerId}
                    onInput={(e) => setManufacturerId(e.target.value)}
                    required
                  >
                    <option value="" disabled>
                      Select manufacturer…
                    </option>
                    {manufacturers.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.legal_name} ({m.prefix})
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="ucx-admin-field">
                  <label>Role fields</label>
                  <input value="(no extra fields)" readOnly />
                </div>
              )}
            </div>

            <div className="ucx-admin-toolbar">
              <div className="ucx-admin-form-actions">
                <button
                  type="submit"
                  className="ucx-btn ucx-btn-primary"
                  disabled={mutating}
                 >
                  {mutating ? "Creating…" : "Create user"}
                </button>
              </div>
            </div>

            {okMsg && <div className="ucx-admin-status-success">{okMsg}</div>}
            {error && <div className="ucx-admin-status-error">{error}</div>}
          </form>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Filters</div>

          <div className="ucx-admin-filters ucx-admin-filters-inventory">
            {view === "users" ? (
              <>
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search (email/display name)" />
                <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
                  <option value="">All roles</option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.name}>
                      {r.name}
                    </option>
                  ))}
                </select>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                  <option value="">All status</option>
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                </select>
              </>
            ) : (
              <>
                <input value={dq} onChange={(e) => setDQ(e.target.value)} placeholder="Search (email/display name)" />
                <select value={dRole} onChange={(e) => setDRole(e.target.value)}>
                  <option value="">All roles</option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.name}>
                      {r.name}
                    </option>
                  ))}
                </select>
                <select value={dStatus} onChange={(e) => setDStatus(e.target.value)}>
                  <option value="">All status</option>
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                </select>
              </>
            )}

            <button
              type="button"
              className="ucx-btn ucx-btn-primary"
              onClick={onApplyFilters}
              disabled={view === "users" ? loading : deletedLoading}
            >
              Apply
            </button>
            <button type="button" className="ucx-btn ucx-btn-outline" onClick={onClearFilters}>
              Clear
            </button>

            <span className="ucx-small">
              {view === "users"
                ? `Showing ${normalizedRows.length} of ${total}`
                : `Showing ${normalizedDeletedRows.length} of ${deletedTotal}`}
            </span>
          </div>
        </div>

        <div className="ucx-admin-table-wrapper">
          <table className="ucx-admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Name</th>
                <th>Created</th>
                {view === "deleted" ? <th>Deleted</th> : null}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {view === "users" && loading && (
                <tr>
                  <td colSpan={7} className="ucx-admin-empty">
                    Loading users…
                  </td>
                </tr>
              )}

              {view === "users" && !loading && normalizedRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="ucx-admin-empty">
                    No users found.
                  </td>
                </tr>
              )}

              {view === "deleted" && deletedLoading && (
                <tr>
                  <td colSpan={8} className="ucx-admin-empty">
                    Loading deleted users…
                  </td>
                </tr>
              )}

              {view === "deleted" && !deletedLoading && normalizedDeletedRows.length === 0 && (
                <tr>
                  <td colSpan={8} className="ucx-admin-empty">
                    No deleted users found.
                  </td>
                </tr>
              )}

              {view === "users" &&
                normalizedRows.map((u) => (
                <React.Fragment key={u.id}>
                  <tr>
                    <td>{u.id}</td>
                    <td>{u.email}</td>
                    <td>{u.role_name || "-"}</td>
                    <td>
                      <div className="ucx-admin-statuscell">
                        <select
                          value={pendingStatusById[u.id] ?? (u.status || "active")}
                          onChange={(e) => onChangeStatus(u.id, e.target.value)}
                          disabled={statusBusyId === u.id}
                        >
                          <option value="active">active</option>
                          <option value="inactive">inactive</option>
                        </select>
                        {statusBusyId === u.id ? <span className="ucx-admin-spinner" aria-label="Saving" /> : null}
                      </div>
                    </td>
                    <td>{u.display_name || "-"}</td>
                    <td>{u.created_at ? new Date(u.created_at).toLocaleString() : "-"}</td>
                    <td>
                      <div className="ucx-admin-actions">
                        <button
                          type="button"
                          className="ucx-admin-btn ucx-admin-btn-approve"
                          onClick={() => setResetOpenId(resetOpenId === u.id ? null : u.id)}
                          disabled={resetBusy && resetOpenId === u.id}
                        >
                          {resetOpenId === u.id ? "Close" : "Change password"}
                        </button>
                        <button
                          type="button"
                          className="ucx-admin-btn ucx-admin-btn-reject"
                          onClick={() => onSoftDeleteUser(u.id, u.email)}
                          disabled={deleteBusyId === u.id}
                        >
                          {deleteBusyId === u.id ? "Deleting…" : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>

                  {resetOpenId === u.id && (
                    <tr>
                      <td colSpan={7}>
                        <div className="ucx-admin-panel" style={{ margin: 0 }}>
                          <div className="ucx-admin-panel-title">Reset password for {u.email}</div>
                          <div className="ucx-admin-toolbar">
                            <div className="ucx-admin-field ucx-admin-field-inline" style={{ flex: 1 }}>
                              <label>New password</label>
                              <input
                                type="password"
                                value={resetPassword}
                                onChange={(e) => setResetPassword(e.target.value)}
                                placeholder="Min 8 chars"
                              />
                            </div>
                            <div className="ucx-admin-form-actions">
                              <button
                                type="button"
                                className="ucx-btn ucx-btn-primary"
                                onClick={() => onResetPassword(u.id)}
                                disabled={resetBusy || resetPassword.length < 8}
                              >
                                {resetBusy ? "Saving…" : "Save"}
                              </button>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}

              {view === "deleted" &&
                normalizedDeletedRows.map((u) => (
                  <tr key={`deleted-${u.id}`}>
                    <td>{u.id}</td>
                    <td>{u.email}</td>
                    <td>{u.role_name || "-"}</td>
                    <td>{u.status || "-"}</td>
                    <td>{u.display_name || "-"}</td>
                    <td>{u.created_at ? new Date(u.created_at).toLocaleString() : "-"}</td>
                    <td>{u.deleted_at ? new Date(u.deleted_at).toLocaleString() : "-"}</td>
                    <td>
                      <div className="ucx-admin-actions">
                        <button
                          type="button"
                          className="ucx-admin-btn ucx-admin-btn-approve"
                          onClick={() => onRestoreUser(u.id, u.email)}
                          disabled={restoreBusyId === u.id}
                        >
                          {restoreBusyId === u.id ? "Restoring…" : "Restore"}
                        </button>
                        <button
                          type="button"
                          className="ucx-admin-btn ucx-admin-btn-reject"
                          onClick={() => onPermanentDeleteUser(u.id, u.email)}
                        >
                          Permanent delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export default AdminUsersPage;
